import { useState } from 'react'
import { Card, Button, Space, Typography, Divider } from 'antd'
import { ExclamationCircleOutlined, BugOutlined, ApiOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { errorService } from '@/services/errorService'
import { dataService } from '@/services/dataService'

const { Title, Paragraph, Text } = Typography

/**
 * ErrorTestPage - Test all error handling scenarios
 *
 * This page tests:
 * 1. API errors (automatically handled by apiService)
 * 2. React crashes (caught by ErrorBoundary)
 * 3. Validation errors (manual errorService calls)
 */
export default function ErrorTestPage() {
  const [crashComponent, setCrashComponent] = useState(false)

  /**
   * Test 1: API Error
   * Trigger an API error by requesting a non-existent endpoint
   */
  const testApiError = async () => {
    console.log('Testing API error...')

    // This will trigger a 404 error, which apiService will display via errorService
    await dataService.search('nonexistent_table', {
      filters: [{ key: 'uid', constraint_type: 'EQUAL', value: 'test' }]
    })
  }

  /**
   * Test 2: Network Error
   * Simulate a network failure by making a request to an invalid URL
   */
  const testNetworkError = async () => {
    console.log('Testing network error...')

    try {
      // This will trigger a network error (CORS or connection failure)
      await fetch('https://invalid-domain-that-does-not-exist-12345.com')
    } catch (error: any) {
      // apiService would normally handle this, but we're testing directly
      errorService.showError(
        'Network Error',
        'Failed to connect to server',
        [
          {
            code: 'NETWORK_ERROR',
            message: error.message || 'Connection failed',
            details: { url: 'https://invalid-domain-that-does-not-exist-12345.com' }
          }
        ]
      )
    }
  }

  /**
   * Test 3: Validation Error
   * Manual call to errorService for validation failures
   */
  const testValidationError = () => {
    console.log('Testing validation error...')

    errorService.showError(
      'Validation Failed',
      'Please check the form fields',
      [
        {
          code: 'VALIDATION_ERROR',
          message: 'Name is required',
          details: { field: 'name', constraint: 'required' }
        },
        {
          code: 'VALIDATION_ERROR',
          message: 'Email must be valid',
          details: { field: 'email', constraint: 'email', value: 'invalid-email' }
        },
        {
          code: 'VALIDATION_ERROR',
          message: 'Price must be greater than 0',
          details: { field: 'price', constraint: 'min', value: -10, min: 0 }
        }
      ]
    )
  }

  /**
   * Test 4: React Component Crash
   * Trigger a React error that will be caught by ErrorBoundary
   */
  const testReactCrash = () => {
    console.log('Testing React crash...')
    setCrashComponent(true)
  }

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2}>
          <BugOutlined /> Error Handling Test Page
        </Title>

        <Paragraph>
          This page tests the universal error handling system. All errors should display
          via <Text code>errorService.showError()</Text> using Ant Design's <Text code>Modal.error()</Text>.
        </Paragraph>

        <Divider />

        <Title level={3}>Test Scenarios</Title>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Test 1: API Error */}
          <Card
            size="small"
            title={
              <span>
                <ApiOutlined /> Test 1: API Error
              </span>
            }
          >
            <Paragraph>
              Triggers a 404 error by requesting a non-existent table.
              <Text code>apiService</Text> will automatically display the error via <Text code>errorService</Text>.
            </Paragraph>
            <Button
              type="primary"
              danger
              icon={<ExclamationCircleOutlined />}
              onClick={testApiError}
            >
              Trigger API Error (404)
            </Button>
          </Card>

          {/* Test 2: Network Error */}
          <Card
            size="small"
            title={
              <span>
                <ApiOutlined /> Test 2: Network Error
              </span>
            }
          >
            <Paragraph>
              Simulates a network failure by connecting to an invalid domain.
              Should display a network error modal with copyable details.
            </Paragraph>
            <Button
              type="primary"
              danger
              icon={<ExclamationCircleOutlined />}
              onClick={testNetworkError}
            >
              Trigger Network Error
            </Button>
          </Card>

          {/* Test 3: Validation Error */}
          <Card
            size="small"
            title={
              <span>
                <CheckCircleOutlined /> Test 3: Validation Error
              </span>
            }
          >
            <Paragraph>
              Manual call to <Text code>errorService.showError()</Text> with multiple validation errors.
              Tests copyable error details with structured data.
            </Paragraph>
            <Button
              type="primary"
              danger
              icon={<ExclamationCircleOutlined />}
              onClick={testValidationError}
            >
              Trigger Validation Error
            </Button>
          </Card>

          {/* Test 4: React Crash */}
          <Card
            size="small"
            title={
              <span>
                <BugOutlined /> Test 4: React Component Crash
              </span>
            }
          >
            <Paragraph>
              Triggers a React component error that will be caught by <Text code>ErrorBoundary</Text>.
              Should display error modal AND fallback UI.
            </Paragraph>
            <Button
              type="primary"
              danger
              icon={<ExclamationCircleOutlined />}
              onClick={testReactCrash}
            >
              Trigger React Crash
            </Button>

            {/* Component that will crash when crashComponent is true */}
            {crashComponent && <CrashingComponent />}
          </Card>
        </Space>

        <Divider />

        <Card
          size="small"
          style={{ backgroundColor: '#f0f9ff', border: '1px solid #91d5ff' }}
        >
          <Title level={4}>Expected Behavior</Title>
          <ul>
            <li>All errors display via <Text code>Modal.error()</Text> with consistent formatting</li>
            <li>Modals are persistent (user must click Close button)</li>
            <li>Error text is copyable via textarea (click to select and copy)</li>
            <li>Error details include timestamp, URL, and user agent</li>
            <li>Console logs show structured error information</li>
          </ul>
        </Card>
      </Card>
    </div>
  )
}

/**
 * Component that intentionally crashes to test ErrorBoundary
 */
function CrashingComponent() {
  // This will throw an error when the component tries to render
  throw new Error('Intentional React component crash for testing ErrorBoundary')

  // eslint-disable-next-line
  return <div>This will never render</div>
}
